'use client';

import { Carousel, CarouselApi, CarouselContent, CarouselItem, useCarousel } from '@/components/ui/carousel-pro';
import { placeHolderImage } from '@/constants/placeHolderImage';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';

const CarouselSlideBtn = () => {
  const { canScrollNext, canScrollPrev, scrollNext, scrollPrev } = useCarousel();

  return (
    <>
      <button
        type='button'
        onClick={scrollPrev}
        className={clsx(
          ' absolute left-0 top-0',
          'h-5 w-full items-center justify-center',
          canScrollPrev ? 'flex' : 'hidden',
          'z-[2] cursor-pointer border-none bg-white/50 p-0 outline-0',
        )}
      >
        <div className='h-[11px] w-[20px]'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='20'
            height='11'
            fill='none'
            viewBox='0 0 20 11'
          >
            <g clipPath='url(#clip0_7579_20982)'>
              <path
                stroke='#4B5563'
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M18.438 9.438 10 1 1.563 9.438'
              ></path>
            </g>
            <defs>
              <clipPath id='clip0_7579_20982'>
                <path
                  fill='#fff'
                  d='M19.438 0v10.438H.563V0z'
                ></path>
              </clipPath>
            </defs>
          </svg>
        </div>
      </button>
      <button
        type='button'
        onClick={scrollNext}
        className={clsx(
          ' absolute bottom-0 left-0',
          'h-5 w-full items-center justify-center',
          canScrollNext ? 'flex' : 'hidden',
          'z-[2] cursor-pointer border-none bg-white/50 p-0 outline-0',
        )}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='20'
          height='11'
          fill='none'
          viewBox='0 0 20 11'
        >
          <g clipPath='url(#clip0_7579_11205)'>
            <path
              stroke='#4B5563'
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M1.563 1.438 10 9.875l8.438-8.437'
            ></path>
          </g>
          <defs>
            <clipPath id='clip0_7579_11205'>
              <path
                fill='#fff'
                d='M.563 10.875V.437h18.875v10.438z'
              ></path>
            </clipPath>
          </defs>
        </svg>
      </button>
    </>
  );
};

interface Props {
  images: any[];
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  videoUrl: string;
  productName: string;
  seoColorLabel: string;
  className?: string;
}

const ProductThumbnails = (props: Props) => {
  const { images, activeIndex, setActiveIndex, videoUrl, className, seoColorLabel, productName } = props;

  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    carouselApi?.scrollTo(activeIndex, false);
  }, [activeIndex, carouselApi]);

  return (
    <div className={cn('text-0 relative', className)}>
      <Carousel
        opts={{
          align: 'start',
        }}
        orientation='vertical'
        setApi={setCarouselApi}
        autoPlayOpts={{ playOnInit: false }}
      >
        <CarouselSlideBtn />
        <CarouselContent className='-mt-2.5 flex h-[710px] w-[120px] flex-col'>
          {images.reduce((acc, item, index, arr) => {
            const imageAlt = [seoColorLabel, productName, index + 1].filter(Boolean).join(' ');

            const isActive = (index < 1 || !videoUrl ? activeIndex : activeIndex - 1) === index;

            acc.push(
              <CarouselItem
                key={index}
                className='flex-none basis-[130px] pt-2.5'
              >
                <div
                  className={clsx(
                    'relative cursor-pointer border-1 border-solid',
                    isActive ? 'border-[#000]' : 'border-transparent',
                  )}
                  onClick={() => {
                    setActiveIndex(!videoUrl || index < 1 ? index : index + 1);
                  }}
                >
                  <Image
                    src={item.image}
                    width={120}
                    height={120}
                    className='h-full w-full object-cover'
                    alt={imageAlt}
                    title={imageAlt}
                    placeholder={placeHolderImage}
                    unoptimized
                  />
                </div>
              </CarouselItem>,
            );

            if (index === 0 && videoUrl) {
              const firstImageAlt = [`Image1`].filter(Boolean).join(' ');

              acc.push(
                <CarouselItem
                  key={arr.length}
                  className='flex-none basis-[130px] pt-2.5'
                >
                  <div
                    className={clsx(
                      '  relative cursor-pointer border-1 border-solid',
                      activeIndex === 1 ? 'border-[#000]' : 'border-transparent',
                    )}
                    onClick={() => {
                      setActiveIndex(1);
                    }}
                  >
                    <div className='absolute left-0 top-0 z-[1] h-full w-full backdrop-blur-[2px]' />

                    <Image
                      src={arr[0].image}
                      width={120}
                      height={120}
                      alt={firstImageAlt}
                      title={firstImageAlt}
                      placeholder={placeHolderImage}
                      unoptimized
                      className='h-full w-full object-cover'
                    />
                    {videoUrl ? (
                      <div className='absolute left-0 top-0 z-[2] flex h-full w-full items-center justify-center'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='50'
                          height='50'
                          fill='none'
                          viewBox='0 0 50 50'
                        >
                          <circle
                            cx='25'
                            cy='25'
                            r='24'
                            stroke='#fff'
                            strokeWidth='2'
                          ></circle>
                          <path
                            fill='#fff'
                            stroke='#fff'
                            d='M33.75 23.701c1 .577 1 2.02 0 2.598l-12 6.928a1.5 1.5 0 0 1-2.25-1.299V18.072a1.5 1.5 0 0 1 2.25-1.3z'
                          ></path>
                        </svg>
                      </div>
                    ) : null}
                  </div>
                </CarouselItem>,
              );
            }

            return acc;
          }, [])}
        </CarouselContent>
      </Carousel>
    </div>
  );
};

export default ProductThumbnails;
